/**
 * disclosure — shared expand/collapse section header primitive
 * (`.sd-disclosure`, css/components/section-header.css), the clickable form
 * of the unified zone header. Its static twin is `.sd-section-header` in the
 * same stylesheet; the two carry identical vertical metrics by contract.
 *
 * Markup contract: `renderDisclosure` emits a real `<button>` carrying
 * `data-disclosure-toggle` + `aria-expanded`, immediately followed by its
 * body element. `wireDisclosures` delegates the click on a caller-supplied
 * root and flips `aria-expanded`/`hidden` on the toggle's `nextElementSibling`.
 * `expandDisclosure` is the one op that force-expands a disclosure by key —
 * used by "jump to this section" links — through the SAME code path a
 * header click takes, then optionally scrolls/focuses the toggle's parent
 * container (the wrapping element the caller rendered `renderDisclosure`'s
 * output into — e.g. TopicDetailView's `[data-sections-anchor]` section —
 * so callers that want scroll/focus behavior give that wrapper
 * `tabindex="-1"`, mirroring the original topic-detail jump).
 *
 * Sharing `onToggle` between `wireDisclosures` and `expandDisclosure`: a
 * `WeakMap` keyed by the root element remembers the `onToggle` a root was
 * wired with, so a later `expandDisclosure(rootEl, key, …)` call on that
 * same root fires the identical `setExpanded` path a header click would —
 * the two can never drift apart. (`wireDisclosures` also returns
 * `{ expand }` as a convenience for callers that already have the wiring
 * result in scope.)
 *
 * `zoneWrapHtml` is the companion for pages whose zones pin: it stamps the
 * wrapper both zone behaviours key on — `.sd-zone-wrap` for the closing-rule
 * grammar and `data-sd-zone` for the pinning — so no page can adopt one and
 * silently miss the other.
 *
 * @module components/disclosure
 */

import { escapeHtml } from '../core/utils.js';

/** Canonical HTML escaper retained as part of the disclosure module API. */
export { escapeHtml } from '../core/utils.js';

/** Root element → the `onToggle` callback it was wired with (or null). */
const rootOnToggle = new WeakMap();

/** Chevron for the disclosure headers; rotates via the toggle's aria-expanded.
 *  Sits immediately right of the title so the two read as one unit. */
const DISCLOSURE_CHEVRON = `
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`;

/**
 * Shared disclosure header (`.sd-disclosure`,
 * css/components/section-header.css): a 20px title + chevron under the
 * section's own hairline rule (carried as the header's `border-top`), on a
 * transparent row that takes a bled cool tint on hover. Clickable to toggle
 * its body. Toggling is delegated via `wireDisclosures` (a real <button>, so
 * keyboard activation is native) and survives async hydration of the body.
 *
 * @param {string} key - Stable id for the disclosure (`data-disclosure-toggle`).
 * @param {string} label - Header text.
 * @param {string} bodyHtml - Collapsible content.
 * @param {{ expanded?: boolean, count?: number|string, aux?: string }} [opts] -
 *   `count` renders the shared at-a-glance chip (`.sd-zone-count`, also used
 *   by static headers): a number for an item count, or a short string where
 *   the section's size is better said another way (a path's `42%`). CSS hides
 *   it while this disclosure is expanded, so the chip needs no JS on toggle.
 *   `aux` is a muted subtitle set beside the label
 *   (`· <aux>`) for headers that name a specific thing rather than a kind —
 *   opt-in, and the one part of the row that truncates when it runs out of
 *   width, so the label and chevron always read whole.
 * @returns {string} HTML fragment (toggle button + body).
 */
export function renderDisclosure(key, label, bodyHtml, { expanded = true, count = null, aux = null } = {}) {
  const hasCount = Number.isFinite(count) || (typeof count === 'string' && count !== '');
  const countChip = hasCount
    ? `<span class="sd-zone-count">${escapeHtml(String(count))}</span>`
    : '';
  const auxSpan = typeof aux === 'string' && aux !== ''
    ? `<span class="sd-disclosure__aux">· ${escapeHtml(aux)}</span>`
    : '';
  return `
    <button type="button" class="sd-disclosure" data-disclosure-toggle="${escapeHtml(key)}" aria-expanded="${expanded}">
      <span class="sd-disclosure__label">${escapeHtml(label)}</span>
      ${auxSpan}
      <span class="sd-disclosure__chevron" aria-hidden="true">${DISCLOSURE_CHEVRON}</span>
      ${countChip}
    </button>
    <div class="sd-disclosure__body"${expanded ? '' : ' hidden'}>
      ${bodyHtml}
    </div>
  `;
}

/**
 * Flip one toggle's expanded state and fire `onToggle` — the single code
 * path both a header click (`wireDisclosures`) and a forced expand
 * (`expandDisclosure`) go through, so they cannot drift.
 *
 * @param {HTMLElement} toggle
 * @param {boolean} expanded
 * @param {((key: string, expanded: boolean) => void)|null} onToggle
 */
function setExpanded(toggle, expanded, onToggle) {
  toggle.setAttribute('aria-expanded', String(expanded));
  const body = toggle.nextElementSibling;
  if (body) body.hidden = !expanded;
  if (onToggle) onToggle(toggle.dataset.disclosureToggle, expanded);
}

/**
 * Wire click delegation for every `[data-disclosure-toggle]` header inside
 * `rootEl` — per-root scoping, safe to call once per panel instance.
 * Registers `onToggle` so a later `expandDisclosure(rootEl, key, …)` call
 * on the same root reuses it.
 *
 * @param {HTMLElement} rootEl
 * @param {{ onToggle?: (key: string, expanded: boolean) => void }} [opts]
 * @returns {{ expand: (key: string, opts?: { scrollIntoView?: boolean, focus?: boolean }) => void }}
 *   Convenience wrapper around `expandDisclosure(rootEl, key, opts)`.
 */
export function wireDisclosures(rootEl, { onToggle = null } = {}) {
  rootOnToggle.set(rootEl, onToggle);
  rootEl.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-disclosure-toggle]');
    if (!toggle || !rootEl.contains(toggle)) return;
    const wasExpanded = toggle.getAttribute('aria-expanded') === 'true';
    setExpanded(toggle, !wasExpanded, onToggle);
  });
  return { expand: (key, opts) => expandDisclosure(rootEl, key, opts) };
}

/**
 * Force-expand one disclosure by key (a no-op if already expanded) through
 * the same `setExpanded` path `wireDisclosures`'s click handler uses — so
 * `onToggle` fires exactly as a header click would — then optionally
 * scroll its wrapping container into view and move focus to it. Mirrors
 * the topic-detail "N sections explored" jump-link behavior.
 *
 * @param {HTMLElement} rootEl - Same root passed to `wireDisclosures`.
 * @param {string} key - The `data-disclosure-toggle` value to expand.
 * @param {{ scrollIntoView?: boolean, focus?: boolean }} [opts]
 */
export function expandDisclosure(rootEl, key, { scrollIntoView = false, focus = false } = {}) {
  const toggle = rootEl.querySelector(`[data-disclosure-toggle="${key}"]`);
  if (!toggle) return;
  if (toggle.getAttribute('aria-expanded') !== 'true') {
    setExpanded(toggle, true, rootOnToggle.get(rootEl) || null);
  }
  const target = toggle.parentElement;
  if (!target) return;
  if (scrollIntoView && target.scrollIntoView) {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }
  if (focus && target.focus) target.focus({ preventScroll: true });
}

/**
 * Wrap one zone — its header and everything the header labels — in the
 * container the zone grammar needs.
 *
 * Two markers, always together. `.sd-zone-wrap` tells
 * components/section-header.css to treat the wrapper as the zone it holds
 * when deciding which end of a band draws its rule; without it the wrapper
 * reads as ordinary content and the previous zone doubles its rule.
 * `data-sd-zone` opts the zone's header into pinning while you are inside it
 * — the wrapper is the containing block that makes the browser do the
 * tracking. A page that wants only the first still gets both, which is the
 * point: the two answer the same question ("where does this zone end?") and a
 * page that wrapped for one reason and not the other was the bug this helper
 * removes.
 *
 * Pinning has preconditions the marker cannot check — see the style guide
 * (§4) for which pages take it and why the rest do not.
 *
 * @param {string} innerHtml - The zone's header and body
 * @param {{ id?: string|null, className?: string }} [opts] - `id` is the jump
 *   target (`expandDisclosure`/`scroll-margin-top` land on the wrapper);
 *   `className` is the page's own class for the zone, if it has one
 * @returns {string} HTML fragment (one wrapper element)
 */
export function zoneWrapHtml(innerHtml, { id = null, className = '' } = {}) {
  const cls = className ? `${className} sd-zone-wrap` : 'sd-zone-wrap';
  const idAttr = id ? ` id="${escapeHtml(id)}"` : '';
  return `<div class="${cls}"${idAttr} data-sd-zone>${innerHtml}</div>`;
}
